import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, Edit3, X } from "lucide-react";

export default function AdminPricingPage() {
  const [config, setConfig] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({ code: "", percent_off: 10, fixed_off_npr: 0, max_uses: 100 });
  const [lmsCourses, setLmsCourses] = useState([]);
  const [editingCourse, setEditingCourse] = useState(null);
  const [courseForm, setCourseForm] = useState({});

  const load = async () => {
    const [p, c, lc] = await Promise.all([
      api.get("/pricing"), api.get("/admin/coupons"), api.get("/admin/courses"),
    ]);
    setConfig(p.data.config);
    setCoupons(c.data);
    setLmsCourses(lc.data);
  };
  useEffect(() => { load(); }, []);

  const saveConfig = async () => {
    try { await api.put("/admin/pricing", { config }); toast.success("Pricing updated"); }
    catch (e) { toast.error("Failed"); }
  };

  const addCoupon = async () => {
    if (!newCoupon.code) { toast.error("Enter code"); return; }
    try {
      await api.post("/admin/coupons", newCoupon);
      toast.success("Coupon created");
      setNewCoupon({ code: "", percent_off: 10, fixed_off_npr: 0, max_uses: 100 });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const delCoupon = async (code) => {
    await api.delete(`/admin/coupons/${code}`);
    toast.success("Deleted"); load();
  };

  const startEditCourse = (c) => {
    setEditingCourse(c.id);
    setCourseForm({
      price_npr: c.price_npr || 0,
      partner_student_pct_off: c.partner_student_pct_off || 0,
      partner_faculty_pct_off: c.partner_faculty_pct_off || 0,
      free_for_partner_students: !!c.free_for_partner_students,
      decision: "approved",
      note: "",
    });
  };

  const saveCoursePricing = async (cid) => {
    try {
      await api.post(`/admin/courses/${cid}/pricing`, courseForm);
      toast.success("Course pricing updated");
      setEditingCourse(null);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  if (!config) return <div className="kb-mono text-xs text-kb-ink-muted">Loading…</div>;

  return (
    <div data-testid="admin-pricing-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">PRICING & COUPONS</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Platform <em className="italic text-kb-primary">pricing config</em></h1>

      <div className="mt-8 kb-card p-6">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">DEFAULT PRICES (NPR)</div>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          {Object.entries(config).filter(([k]) => k !== "currency").map(([k, v]) => (
            <div key={k}>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">{k.replace(/_/g, " ")}</label>
              <input type="number" className="kb-input mt-1.5" value={v}
                onChange={(e) => setConfig({ ...config, [k]: (e.target.value === "" ? "" : (Number(e.target.value) || 0)) })}
                data-testid={`pricing-${k}`} />
            </div>
          ))}
        </div>
        <button onClick={saveConfig} className="kb-btn-primary mt-5" data-testid="pricing-save-btn">
          <Save className="w-4 h-4" /> Save pricing
        </button>
      </div>

      <div className="mt-8 kb-card p-6" data-testid="admin-lms-pricing">
        <div className="flex items-baseline justify-between">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">LMS COURSE PRICING (ADMIN APPROVED)</div>
          <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light">{lmsCourses.length} course{lmsCourses.length === 1 ? "" : "s"}</span>
        </div>
        <p className="text-xs text-kb-ink-muted mt-2">
          Authors propose course pricing while authoring — you finalise the price + partner-institution discounts here. Changes take effect immediately.
        </p>
        <div className="mt-4 space-y-2">
          {lmsCourses.length === 0 && <div className="text-sm text-kb-ink-muted text-center py-6">No courses yet.</div>}
          {lmsCourses.map((c) => (
            <div key={c.id} className="border border-slate-200 rounded-sm p-3" data-testid={`lms-pricing-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/dashboard/courses/${c.id}/edit`} className="font-medium text-kb-ink hover:text-kb-primary">{c.title}</Link>
                    <span className={`kb-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm border ${c.status === "published" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>{c.status}</span>
                    {c.pricing_status && (
                      <span className={`kb-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm border ${c.pricing_status === "approved" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : c.pricing_status === "rejected" ? "border-rose-200 bg-rose-50 text-rose-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>{c.pricing_status}</span>
                    )}
                  </div>
                  <div className="kb-mono text-[10px] text-kb-ink-light mt-1">by {c.owner_name || "—"} · {c.enrolled_count || 0} enrolled</div>
                </div>
                {editingCourse !== c.id ? (
                  <div className="text-right">
                    <div className="font-mono text-kb-primary text-sm">NPR {(c.price_npr || 0).toLocaleString()}</div>
                    {(c.partner_student_pct_off || c.partner_faculty_pct_off || c.free_for_partner_students) && (
                      <div className="kb-mono text-[10px] text-emerald-700">
                        {c.free_for_partner_students ? "FREE for partners" : `${c.partner_student_pct_off || 0}% stu · ${c.partner_faculty_pct_off || 0}% fac`}
                      </div>
                    )}
                    <button onClick={() => startEditCourse(c)} className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1 mt-1" data-testid={`lms-pricing-${c.id}-edit`}>
                      <Edit3 className="w-3 h-3" /> Edit pricing
                    </button>
                  </div>
                ) : null}
              </div>

              {editingCourse === c.id && (
                <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Base price NPR</label>
                    <input type="number" min="0" className="kb-input text-sm mt-1" value={courseForm.price_npr}
                      onChange={(e) => setCourseForm({ ...courseForm, price_npr: (e.target.value === "" ? "" : (Number(e.target.value) || 0)) })}
                      data-testid={`lms-pricing-${c.id}-price`} />
                  </div>
                  <div>
                    <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Student % off</label>
                    <input type="number" min="0" max="100" className="kb-input text-sm mt-1" value={courseForm.partner_student_pct_off}
                      onChange={(e) => setCourseForm({ ...courseForm, partner_student_pct_off: (e.target.value === "" ? "" : (Number(e.target.value) || 0)) })} />
                  </div>
                  <div>
                    <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Faculty % off</label>
                    <input type="number" min="0" max="100" className="kb-input text-sm mt-1" value={courseForm.partner_faculty_pct_off}
                      onChange={(e) => setCourseForm({ ...courseForm, partner_faculty_pct_off: (e.target.value === "" ? "" : (Number(e.target.value) || 0)) })} />
                  </div>
                  <div>
                    <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Free for partners</label>
                    <label className="inline-flex items-center gap-2 mt-2 text-xs">
                      <input type="checkbox" checked={!!courseForm.free_for_partner_students}
                        onChange={(e) => setCourseForm({ ...courseForm, free_for_partner_students: e.target.checked })} /> Enable
                    </label>
                  </div>
                  <input className="kb-input text-sm col-span-2 lg:col-span-3" placeholder="Note (optional)"
                    value={courseForm.note} onChange={(e) => setCourseForm({ ...courseForm, note: e.target.value })} />
                  <div className="col-span-2 lg:col-span-1 flex gap-2">
                    <button onClick={() => saveCoursePricing(c.id)} className="kb-btn-primary text-sm flex-1" data-testid={`lms-pricing-${c.id}-save`}>Save</button>
                    <button onClick={() => setEditingCourse(null)} className="kb-btn-outline text-sm" data-testid={`lms-pricing-${c.id}-cancel`}><X className="w-3 h-3" /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 kb-card p-6">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">COUPONS</div>
        <div className="mt-4 grid grid-cols-12 gap-2">
          <input placeholder="CODE" className="kb-input sm:col-span-3 font-mono uppercase"
            value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
            data-testid="coupon-code-input" />
          <input type="number" placeholder="% off" className="kb-input sm:col-span-2"
            value={newCoupon.percent_off} onChange={(e) => setNewCoupon({ ...newCoupon, percent_off: (e.target.value === "" ? "" : (Number(e.target.value) || 0)) })}
            data-testid="coupon-percent-input" />
          <input type="number" placeholder="Fixed off NPR" className="kb-input sm:col-span-2"
            value={newCoupon.fixed_off_npr} onChange={(e) => setNewCoupon({ ...newCoupon, fixed_off_npr: (e.target.value === "" ? "" : (Number(e.target.value) || 0)) })}
            data-testid="coupon-fixed-input" />
          <input type="number" placeholder="Max uses" className="kb-input sm:col-span-2"
            value={newCoupon.max_uses} onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: (e.target.value === "" ? "" : (Number(e.target.value) || 100)) })}
            data-testid="coupon-max-uses-input" />
          <button onClick={addCoupon} className="kb-btn-primary sm:col-span-3" data-testid="coupon-add-btn">
            <Plus className="w-4 h-4" /> Add coupon
          </button>
        </div>
        <div className="mt-5 space-y-2">
          {coupons.length === 0 ? <div className="text-sm text-kb-ink-muted">No coupons yet.</div> :
            coupons.map((c) => (
              <div key={c.code} className="border border-slate-200 rounded-sm p-3 flex items-center gap-3" data-testid={`coupon-${c.code}`}>
                <span className="font-mono text-kb-primary">{c.code}</span>
                <span className="kb-mono text-xs text-kb-ink-muted">{c.percent_off || 0}% off · NPR {c.fixed_off_npr || 0} off · {c.used_count}/{c.max_uses}</span>
                <button onClick={() => delCoupon(c.code)} className="ml-auto text-rose-600" data-testid={`coupon-${c.code}-delete`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
